import { useState, useEffect, useCallback } from 'react';
import type {
  ConversationSummary,
  NewMessageEvent,
  ConversationUpdatedEvent,
  UnreadCountEvent,
} from '@tabbio/types';
import { SOCKET_EVENTS } from '@tabbio/types';
import { fetchConversations } from '../lib/messaging-api';
import { isInboxConversation } from '../lib/messaging-visibility';
import { useSocket } from './useSocket';
import { useAuth } from './useAuth';
import { useWorkspace } from '../providers/WorkspaceProvider.shared';

/**
 * Hook for fetching and real-time updating of conversation list.
 */
export function useConversations() {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { socket } = useSocket();
  const { user } = useAuth();
  const { mode, companyId } = useWorkspace();
  const currentUserId = user?.id ?? '';

  // Initial fetch (re-runs when active workspace changes)
  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await fetchConversations({ mode, companyId });
      // Server filtering is the source of truth; keep this backstop so native
      // and web also hide empty legacy rows from an older API deployment/cache.
      setConversations(result.conversations.filter(isInboxConversation));
    } catch (e: any) {
      setError(e.message ?? 'Failed to load conversations');
    } finally {
      setIsLoading(false);
    }
  }, [mode, companyId]);

  useEffect(() => {
    load();
  }, [load]);

  // Real-time updates via socket
  const activeCompanyId = mode === 'employer' ? companyId ?? null : null;
  useEffect(() => {
    if (!socket) return;

    const handleConversationUpdated = (event: ConversationUpdatedEvent) => {
      setConversations((prev) => {
        if (!isInboxConversation(event.conversation)) {
          return prev.filter((c) => c.id !== event.conversation.id);
        }
        const idx = prev.findIndex((c) => c.id === event.conversation.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = event.conversation;
          return sortByLastMessage(next);
        }
        // New conversation: only add to top if it belongs to the active workspace.
        // Cross-workspace events for a known-to-us user must not leak into this list.
        const eventCompanyId = event.conversation.companyId ?? null;
        if (eventCompanyId !== activeCompanyId) {
          return prev;
        }
        return [event.conversation, ...prev];
      });
    };

    const handleNewMessage = (event: NewMessageEvent) => {
      setConversations((prev) => {
        return sortByLastMessage(
          prev.map((c) => {
            if (c.id !== event.conversationId) return c;
            return {
              ...c,
              lastMessage: event.message,
              lastMessageAt: event.message.createdAt,
              participants: c.participants.map((participant) => {
                if (
                  !currentUserId ||
                  participant.userId !== currentUserId ||
                  event.message.senderId === currentUserId
                ) {
                  return participant;
                }
                return {
                  ...participant,
                  unreadCount: participant.unreadCount + 1,
                };
              }),
            };
          }),
        );
      });
    };

    const handleUnreadCount = (event: UnreadCountEvent) => {
      setConversations((prev) =>
        prev.map((conversation) => {
          if (conversation.id !== event.conversationId) return conversation;
          return {
            ...conversation,
            participants: conversation.participants.map((participant) =>
              participant.userId === currentUserId
                ? { ...participant, unreadCount: event.count }
                : participant,
            ),
          };
        }),
      );
    };

    socket.on(SOCKET_EVENTS.CONVERSATION_UPDATED, handleConversationUpdated);
    socket.on(SOCKET_EVENTS.NEW_MESSAGE, handleNewMessage);
    socket.on(SOCKET_EVENTS.UNREAD_COUNT, handleUnreadCount);

    return () => {
      socket.off(SOCKET_EVENTS.CONVERSATION_UPDATED, handleConversationUpdated);
      socket.off(SOCKET_EVENTS.NEW_MESSAGE, handleNewMessage);
      socket.off(SOCKET_EVENTS.UNREAD_COUNT, handleUnreadCount);
    };
  }, [currentUserId, socket, activeCompanyId]);

  return { conversations, isLoading, error, refetch: load };
}

function sortByLastMessage(list: ConversationSummary[]): ConversationSummary[] {
  return [...list].sort((a, b) => {
    const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
    const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
    return bTime - aTime;
  });
}
